import { FC, useState, useCallback, useEffect } from 'react'
import { isEmpty } from 'ramda'

import BaseDialog from '~/src/components/shared/BaseDialog'

import useBugReportQuery from '~/src/components/ReportIssue/hook'
import useTrackCvtEvent from '~/src/hooks/useTrackCvtEvent'
import useTranslation from '~/src/hooks/useTranslation'

import CreateTaskStep from '~/src/components/ReportIssue/CreateTaskStep'
import { MixpanelEvent } from '~/clients/mixpanelClient'

import { Option, Tag } from '~/src/components/ReportIssue/types'

const OPTIONS: Array<Option> = [
  {
    value: 'Select an option',
    tag: Tag.BUG,
  },
  { value: 'Report a bug', tag: Tag.BUG },
  { value: 'Request a feature', tag: Tag.FEATURE },
  { value: 'Feedback', tag: Tag.FEEDBACK },
]

type Props = {
  isRadiusUser: boolean
  isOpen: boolean
  isV2: boolean
  onCloseHandler: () => void
}

const ReportIssue: FC<Props> = ({ isRadiusUser, isOpen, isV2, onCloseHandler }) => {
  const { t } = useTranslation('common')
  const [details, setDetails] = useState('')
  const [subject, setTitle] = useState('')
  const [selectedOption, setSelectedOption] = useState<Option>(
    isV2 ? OPTIONS[2] : OPTIONS[0]
  )
  const [validationError, setValidationError] = useState('')
  const trackCvtEvent = useTrackCvtEvent()

  const [submitBugReportHandler, { isLoading, status, reset }] = useBugReportQuery(
    isRadiusUser ? 'RADLOG' : 'CVT'
  )

  useEffect(() => {
    if (status === 'success') {
      onCloseHandler()
    }
  }, [status, onCloseHandler])

  const resetState = useCallback(() => {
    setTitle('')
    setDetails('')
    setSelectedOption(OPTIONS[0])
    setValidationError('')
    reset()
  }, [reset])

  const submitBugReport = useCallback(() => {
    if (isEmpty(subject)) {
      setValidationError(t('reportIssue.subjectRequired'))

      return
    }

    submitBugReportHandler(
      {
        subject,
        details,
        tag: selectedOption.tag || Tag.BUG,
      },
      () => trackCvtEvent(MixpanelEvent.CVTFeedbackSubmitted)
    )
  }, [subject, details, selectedOption, t, submitBugReportHandler, trackCvtEvent])

  const onCloseDialog = useCallback(() => {
    if (!isLoading) {
      trackCvtEvent(MixpanelEvent.CVTFeedbackCancelled)
    }
    resetState()
    onCloseHandler()
  }, [isLoading, resetState, onCloseHandler, trackCvtEvent])

  const onOptionChange = useCallback(
    (option: Option) => {
      setSelectedOption(option)
    },
    [setSelectedOption]
  )

  const handleSubjectChange = (value: string) => {
    setTitle(value)
    setValidationError('')
  }

  return (
    <BaseDialog
      className="w-[25rem]"
      isOpen={isOpen}
      loading={isLoading}
      primaryButtonText={t('buttons.submit')}
      secondaryButtonText={t('buttons.cancel')}
      title={isV2 ? t('nav.provideFeedback') : t('reportIssue.reportAnIssue')}
      onClose={onCloseDialog}
      onConfirm={submitBugReport}
    >
      <CreateTaskStep
        isError={status === 'error'}
        isV2
        options={OPTIONS.slice(1, OPTIONS.length)}
        selectedOption={selectedOption}
        validationError={validationError}
        onDetailsChanged={setDetails}
        onOptionChange={onOptionChange}
        onSubjectChanged={handleSubjectChange}
      />
    </BaseDialog>
  )
}

export default ReportIssue
